import { Controller } from "@hotwired/stimulus"
import confetti from "canvas-confetti"

// Auto-opens the Send-a-Gift success modal when the order page is loaded with a
// `completed` build param, fires a celebratory confetti burst, and strips the
// param from the URL so a refresh does not re-trigger the modal.
export default class extends Controller {
  static get targets() {
    return ["confetti"]
  }

  connect() {
    this.stripCompletedParam()
    this._confettiActive = false

    const $modal = $(this.element)
    // Fire once the modal is fully shown so the canvas has its final dimensions.
    $modal.one("shown.bs.modal", () => this.fireConfetti())
    $modal.modal("show")
  }

  disconnect() {
    this._confettiActive = false
    if (this._confettiFrame) {
      cancelAnimationFrame(this._confettiFrame)
      this._confettiFrame = null
    }
    $(this.element).modal("hide")
  }

  // Rains confetti from the top edge to the bottom, confined to the in-modal
  // canvas (confetti.create binds the animation to that element; the modal's
  // overflow:hidden clips anything that drifts past its edges).
  fireConfetti() {
    if (!this.hasConfettiTarget) {
      return
    }

    const cannon = confetti.create(this.confettiTarget, { resize: true })
    const colors = ["#FF6B9D", "#4FC3F7", "#F26D5B", "#7ED957", "#FFC43A"]
    const durationMs = 2000
    const end = Date.now() + durationMs
    this._confettiActive = true

    const rain = () => {
      if (!this._confettiActive) {
        return
      }
      cannon({
        particleCount: 2,
        angle: 270, // straight down
        spread: 70,
        startVelocity: 18,
        gravity: 1.1,
        ticks: 200,
        scalar: 0.9,
        drift: (Math.random() - 0.5) * 0.8,
        origin: { x: Math.random(), y: -0.1 }, // spawn across the top, just out of view
        colors,
        disableForReducedMotion: true,
      })

      if (Date.now() < end && this._confettiActive) {
        this._confettiFrame = requestAnimationFrame(rain)
      } else {
        this._confettiFrame = null
        this._confettiActive = false
      }
    }

    rain()
  }

  stripCompletedParam() {
    const url = new URL(window.location.href)
    if (url.searchParams.has("completed")) {
      url.searchParams.delete("completed")
      window.history.replaceState({}, document.title, url.toString())
    }
  }

  // Closes the modal and brings a section of the page into view, e.g. the order's links table.
  // Focus moves there too, so keyboard and screen reader users land on what they asked to see.
  showSection({ params: { section } }) {
    const target = document.getElementById(section)
    $(this.element).one("hidden.bs.modal", () => {
      if (!target) {
        return
      }
      target.setAttribute("tabindex", "-1")
      target.scrollIntoView({ behavior: "smooth", block: "start" })
      target.focus({ preventScroll: true })
    })
    $(this.element).modal("hide")
  }
}
